export type SeoLanguage = "sv" | "en";

const MIN_TITLE_LENGTH = 30;
// Google renders roughly 55-60 characters of a Swedish title before cutting it
// off. The previous budget of 70 was above that line, so a long title was
// shortened here, given an ellipsis, had " | Caire" appended, and then got
// truncated a *second* time in the SERP — the reader saw a headline damaged
// twice. Titles are shortened to fit this budget by `shortenTitle` below.
const MAX_TITLE_LENGTH = 60;
const MIN_DESCRIPTION_LENGTH = 70;
// Left at 165 deliberately. Google shows ~155-160 characters, but tightening
// this budget makes things worse, not better: every description between the new
// budget and the old one starts being cut *here*, gaining our ellipsis, where
// before it reached the reader whole and Google decided where to stop. Trimming
// a description is only an improvement when it removes an ellipsis; lowering
// the number adds them.
const MAX_DESCRIPTION_LENGTH = 165;
// Only end a description early on a sentence boundary when that sentence lands
// this far in. Gating on MIN_DESCRIPTION_LENGTH (the *padding* floor for short
// copy) instead would cut at the first full stop past 70 characters: measured
// across the locale files, 143 descriptions would have shipped at ~71-75
// characters where they previously shipped ~155-165 — halving the SERP snippet
// on a site whose measured problem is click-through rate.
const MIN_SENTENCE_CUT = 130;

/**
 * Tried in order, so a dash splits before a colon: "Komplett guide:
 * AI-implementering i hemtjänsten - Från planering till framgång" keeps both
 * the label and the subject rather than collapsing to "Komplett guide".
 */
const CLAUSE_SEPARATORS = [" — ", " – ", " - ", ": ", "? ", "! "];
/** Below this, a leading clause is too stubby to describe the page. */
const MIN_CLAUSE_LENGTH = 18;

/** Trims to `maxLength` on a word boundary, with no ellipsis appended. */
function cutAtWholeWord(value: string, maxLength: number) {
  const trimmed = value.trim();
  if (trimmed.length <= maxLength) return trimmed;

  const candidate = trimmed.slice(0, maxLength);
  const lastWordBoundary = candidate.lastIndexOf(" ");
  return candidate.slice(0, Math.max(lastWordBoundary, 1)).trimEnd();
}

/**
 * Shortens a title to `maxLength` by dropping trailing clauses rather than
 * cutting mid-phrase.
 *
 * "Personalplanering i hemtjänsten: från bemanningsgap till bättre brukartid"
 * becomes "Personalplanering i hemtjänsten" — the keyword-bearing head of the
 * title — instead of "Personalplanering i hemtjänsten: från bemanningsgap
 * till…". The page's own <h1> keeps the full sentence; only the SERP headline
 * is shortened, and no ellipsis is emitted: where Google still needs to cut, it
 * appends its own, and a clean phrase reads better than one ending in "…".
 */
function shortenTitle(value: string, maxLength: number) {
  const trimmed = value.trim();
  if (trimmed.length <= maxLength) return trimmed;

  for (const separator of CLAUSE_SEPARATORS) {
    const head = trimmed.split(separator)[0]?.trim();
    // "? " and "! " belong to the clause they close, unlike ":" or a dash.
    const keepsTerminator =
      separator.startsWith("?") || separator.startsWith("!");
    const candidate =
      head && keepsTerminator ? `${head}${separator.trim()}` : head;
    if (
      candidate &&
      candidate.length >= MIN_CLAUSE_LENGTH &&
      candidate.length <= maxLength
    ) {
      return candidate;
    }
  }

  return cutAtWholeWord(trimmed, maxLength);
}

function truncateAtWordBoundary(value: string, maxLength: number) {
  const trimmed = value.trim();
  if (trimmed.length <= maxLength) return trimmed;

  const candidate = trimmed.slice(0, maxLength - 1);
  const lastWordBoundary = candidate.lastIndexOf(" ");
  return `${candidate.slice(0, Math.max(lastWordBoundary, 1)).trimEnd()}…`;
}

/**
 * Keeps every title useful in a SERP without stripping its brand suffix. A
 * generic context is added only to labels too short to communicate page intent.
 */
export function formatSeoTitle(
  title: string,
  siteName: string,
  language: SeoLanguage,
) {
  const brandSuffix = ` | ${siteName}`;
  let cleanTitle = title.replace(/\s*\|\s*caire\s*$/i, "").trim();
  const alreadyBranded = new RegExp(`\\b${siteName}\\b`, "i").test(cleanTitle);
  const suffix = alreadyBranded ? "" : brandSuffix;
  const budget = MAX_TITLE_LENGTH - suffix.length;

  if (`${cleanTitle}${suffix}`.length < MIN_TITLE_LENGTH) {
    cleanTitle +=
      language === "en"
        ? " – home care planning"
        : " – planering för hemtjänsten";
  }

  let shortened = shortenTitle(cleanTitle, budget);
  // A clause head can come back too short to describe the page at all — the
  // separator sat near the start of the title. Only heads between
  // MIN_CLAUSE_LENGTH and MIN_TITLE_LENGTH trip this; longer heads are kept
  // even when they leave budget unspent, because a clean phrase beats a cut
  // ending on a conjunction ("VRPTW hemtjänstoptimering | Caire", not
  // "VRPTW hemtjänstoptimering: besökstäckning och | Caire").
  if (`${shortened}${suffix}`.length < MIN_TITLE_LENGTH) {
    shortened = cutAtWholeWord(cleanTitle, budget);
  }

  return `${shortened}${suffix}`;
}

/**
 * Meta descriptions are concise search-result summaries. Long source copy is
 * shortened at a word boundary; short labels receive explicit Caire context.
 */
export function formatSeoDescription(
  description: string,
  language: SeoLanguage,
) {
  let formatted = description.trim();

  if (formatted.length < MIN_DESCRIPTION_LENGTH) {
    formatted = `${formatted} ${
      language === "en"
        ? "Explore how Caire supports secure, planner-controlled home care scheduling."
        : "Utforska hur Caire ger tryggt, planerarstyrt stöd för hemtjänstens schemaläggning."
    }`.trim();
  }

  if (formatted.length <= MAX_DESCRIPTION_LENGTH) return formatted;

  // Prefer ending on a complete sentence: a summary that stops at a full stop
  // reads as written, where one stopping at "…" reads as cut off. Only worth it
  // when the sentence lands near the budget — see MIN_SENTENCE_CUT.
  const withinBudget = formatted.slice(0, MAX_DESCRIPTION_LENGTH);
  const lastSentenceEnd = Math.max(
    withinBudget.lastIndexOf(". "),
    withinBudget.lastIndexOf("! "),
    withinBudget.lastIndexOf("? "),
    // A sentence ending exactly on the budget boundary has its following space
    // outside the slice, so the lookups above miss it — and the description
    // would take an ellipsis while a complete sentence filling the whole budget
    // was available, the worst of both outcomes.
    /[.!?]$/.test(withinBudget) ? withinBudget.length - 1 : -1,
  );
  if (lastSentenceEnd >= MIN_SENTENCE_CUT) {
    return withinBudget.slice(0, lastSentenceEnd + 1).trimEnd();
  }

  return truncateAtWordBoundary(formatted, MAX_DESCRIPTION_LENGTH);
}

// Marketing/tracking query params (CTA attribution, campaign source, contact
// subject prefill, …) never change page content, so canonical and hreflang
// always point at the clean path. A URL carrying only these params does not
// self-reference its own canonical, which crawlers that fetch it directly flag
// as a hreflang/canonical conflict. Such a URL is served `noindex,follow`
// without hreflang: crawlable (link equity and attribution still work) but
// never indexed as a variant. `source` is the internal book-demo attribution
// parameter (see `bookDemoHrefWithSource`).
const TRACKING_ONLY_PARAM_PATTERN =
  /^(?:intent|locale|analysis_|subject|utm_|source$)/;

/** True when `search` carries at least one parameter, and only tracking ones. */
export function hasOnlyTrackingParams(search: string): boolean {
  const queryKeys = Array.from(new URLSearchParams(search).keys());
  return (
    queryKeys.length > 0 &&
    queryKeys.every((key) => TRACKING_ONLY_PARAM_PATTERN.test(key))
  );
}
